import type { SiteDocument, SiteNode, Page, ComponentType } from "@zirro/schema";
import { newNodeId, createNode } from "@zirro/schema";

export interface NodeSearchResult {
  node: SiteNode;
  parent: SiteNode | Page | null;
  container: SiteNode[];
  index: number;
}

/**
 * Recursively search document nodes for a specific nodeId.
 * Returns node, parent, parent container array reference, and index within container.
 */
export function findNodeById(
  document: SiteDocument,
  nodeId: string,
): NodeSearchResult | null {
  // Check header and header children
  if (document.header) {
    if (document.header.id === nodeId) {
      return {
        node: document.header,
        parent: null,
        container: [],
        index: -1,
      };
    }
  }

  // Check footer
  if (document.footer) {
    if (document.footer.id === nodeId) {
      return {
        node: document.footer,
        parent: null,
        container: [],
        index: -1,
      };
    }
  }

  // Search each page
  for (const page of document.pages) {
    const result = searchNodesInArray(page.nodes, page, nodeId);
    if (result) return result;
  }

  return null;
}

function searchNodesInArray(
  nodes: SiteNode[],
  parent: SiteNode | Page,
  targetId: string,
): NodeSearchResult | null {
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    if (n.id === targetId) {
      return { node: n, parent, container: nodes, index: i };
    }
    if (n.children && n.children.length > 0) {
      const childRes = searchNodesInArray(n.children, n, targetId);
      if (childRes) return childRes;
    }
  }
  return null;
}

/**
 * Deep clones a SiteNode tree while assigning brand-new unique node IDs
 * to all nodes and their descendant children.
 */
export function cloneNodeWithNewIds(node: SiteNode): SiteNode {
  const clonedProps = JSON.parse(JSON.stringify(node.props ?? {}));
  const clonedOverrides = node.overrides
    ? JSON.parse(JSON.stringify(node.overrides))
    : undefined;

  const newNode: SiteNode = {
    id: newNodeId(),
    type: node.type,
    props: clonedProps,
    overrides: clonedOverrides,
  };

  if (node.children && node.children.length > 0) {
    newNode.children = node.children.map((child) => cloneNodeWithNewIds(child));
  }

  return newNode;
}

/**
 * Clones a node tree with fresh ids, keeping any role suffix after "__"
 * (e.g. "row__products" -> "n_abc…__products"). Suffixes mark nodes the
 * host app fills with live content, so they must survive re-identification.
 */
export function cloneNodeKeepingRoles(node: SiteNode): SiteNode {
  const roleAt = node.id.lastIndexOf("__");
  const role = roleAt > 0 ? node.id.slice(roleAt) : "";
  const cloned: SiteNode = {
    ...(JSON.parse(JSON.stringify(node)) as SiteNode),
    id: `${newNodeId()}${role}`.slice(0, 64),
  };
  if (node.children && node.children.length > 0) {
    cloned.children = node.children.map((child) => cloneNodeKeepingRoles(child));
  }
  return cloned;
}

/**
 * Returns a new document with the target node's props updated.
 */
export function updateNodePropsInDocument(
  document: SiteDocument,
  nodeId: string,
  updater: (prevProps: Record<string, unknown>, prevOverrides?: SiteNode["overrides"]) => {
    props: Record<string, unknown>;
    overrides?: SiteNode["overrides"];
  },
): SiteDocument {
  const updateNodes = (nodes: SiteNode[]): SiteNode[] => {
    return nodes.map((node) => {
      if (node.id === nodeId) {
        const { props, overrides } = updater(node.props, node.overrides);
        return { ...node, props, overrides };
      }
      if (node.children && node.children.length > 0) {
        return { ...node, children: updateNodes(node.children) };
      }
      return node;
    });
  };

  const newPages = document.pages.map((page) => ({
    ...page,
    nodes: updateNodes(page.nodes),
  }));

  let newHeader = document.header;
  if (document.header && document.header.id === nodeId) {
    const { props, overrides } = updater(document.header.props, document.header.overrides);
    newHeader = { ...document.header, props, overrides };
  } else if (document.header?.children) {
    newHeader = { ...document.header, children: updateNodes(document.header.children) };
  }

  let newFooter = document.footer;
  if (document.footer && document.footer.id === nodeId) {
    const { props, overrides } = updater(document.footer.props, document.footer.overrides);
    newFooter = { ...document.footer, props, overrides };
  } else if (document.footer?.children) {
    newFooter = { ...document.footer, children: updateNodes(document.footer.children) };
  }

  return {
    ...document,
    pages: newPages,
    header: newHeader,
    footer: newFooter,
  };
}

/**
 * Appends or inserts a new component node into a target parent container or page root.
 */
export function addNodeToDocument(
  document: SiteDocument,
  parentContainerId: string | null,
  activePageId: string,
  componentType: ComponentType,
  targetIndex?: number,
): { document: SiteDocument; newNode: SiteNode } {
  const newNode = createNode(componentType);

  if (!parentContainerId) {
    // Insert into active page root
    const newPages = document.pages.map((page) => {
      if (page.id !== activePageId) return page;
      const nodes = [...page.nodes];
      const idx = targetIndex ?? nodes.length;
      nodes.splice(idx, 0, newNode);
      return { ...page, nodes };
    });
    return { document: { ...document, pages: newPages }, newNode };
  }

  // Insert into specified parent container node
  const insertIntoNodes = (nodes: SiteNode[]): SiteNode[] => {
    return nodes.map((node) => {
      if (node.id === parentContainerId) {
        const children = [...(node.children ?? [])];
        const idx = targetIndex ?? children.length;
        children.splice(idx, 0, newNode);
        return { ...node, children };
      }
      if (node.children && node.children.length > 0) {
        return { ...node, children: insertIntoNodes(node.children) };
      }
      return node;
    });
  };

  const newPages = document.pages.map((page) => ({
    ...page,
    nodes: insertIntoNodes(page.nodes),
  }));

  return { document: { ...document, pages: newPages }, newNode };
}

/**
 * Inserts a complete pre-built Section subtree into the active page root.
 * Generates fresh unique node IDs for all nodes in the tree.
 */
export function insertSectionTreeToDocument(
  document: SiteDocument,
  activePageId: string,
  sectionTree: SiteNode,
  targetIndex?: number,
): { document: SiteDocument; newSection: SiteNode } {
  const newSection = cloneNodeWithNewIds(sectionTree);
  const newPages = document.pages.map((page) => {
    if (page.id !== activePageId) return page;
    const nodes = [...page.nodes];
    const idx = targetIndex ?? nodes.length;
    nodes.splice(idx, 0, newSection);
    return { ...page, nodes };
  });
  return { document: { ...document, pages: newPages }, newSection };
}

/**
 * Removes a node by ID from the document tree.
 */
export function removeNodeFromDocument(
  document: SiteDocument,
  nodeId: string,
): SiteDocument {
  const removeNodes = (nodes: SiteNode[]): SiteNode[] => {
    return nodes
      .filter((n) => n.id !== nodeId)
      .map((n) => (n.children ? { ...n, children: removeNodes(n.children) } : n));
  };

  const newPages = document.pages.map((page) => ({
    ...page,
    nodes: removeNodes(page.nodes),
  }));

  return {
    ...document,
    pages: newPages,
  };
}

/**
 * Duplicates a node by ID and places the cloned node right after it in the parent container.
 */
export function duplicateNodeInDocument(
  document: SiteDocument,
  nodeId: string,
): { document: SiteDocument; newNode: SiteNode | null } {
  const search = findNodeById(document, nodeId);
  if (!search) return { document, newNode: null };

  const cloned = cloneNodeWithNewIds(search.node);

  const duplicateInNodes = (nodes: SiteNode[]): SiteNode[] => {
    const result: SiteNode[] = [];
    for (const n of nodes) {
      if (n.id === nodeId) {
        result.push(n);
        result.push(cloned);
      } else {
        if (n.children && n.children.length > 0) {
          result.push({ ...n, children: duplicateInNodes(n.children) });
        } else {
          result.push(n);
        }
      }
    }
    return result;
  };

  const newPages = document.pages.map((page) => ({
    ...page,
    nodes: duplicateInNodes(page.nodes),
  }));

  return { document: { ...document, pages: newPages }, newNode: cloned };
}

/**
 * Moves a node up or down within its parent container array.
 */
export function moveNodeInDocument(
  document: SiteDocument,
  nodeId: string,
  direction: "up" | "down",
): SiteDocument {
  const moveInNodes = (nodes: SiteNode[]): SiteNode[] => {
    const idx = nodes.findIndex((n) => n.id === nodeId);
    if (idx !== -1) {
      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= nodes.length) return nodes;
      const copy = [...nodes];
      const [moved] = copy.splice(idx, 1);
      copy.splice(targetIdx, 0, moved);
      return copy;
    }
    return nodes.map((n) => {
      if (n.children && n.children.length > 0) {
        return { ...n, children: moveInNodes(n.children) };
      }
      return n;
    });
  };

  const newPages = document.pages.map((page) => ({
    ...page,
    nodes: moveInNodes(page.nodes),
  }));

  return { ...document, pages: newPages };
}

/**
 * Reorders a node relative to a target node (before, after, or inside if container).
 */
export function reorderNodeInDocument(
  document: SiteDocument,
  sourceId: string,
  targetId: string,
  position: "before" | "after" | "inside",
): SiteDocument {
  if (sourceId === targetId) return document;
  const sourceSearch = findNodeById(document, sourceId);
  if (!sourceSearch) return document;

  const nodeToMove = sourceSearch.node;
  const docWithoutSource = removeNodeFromDocument(document, sourceId);

  const targetSearch = findNodeById(docWithoutSource, targetId);
  if (!targetSearch) return document;

  if (
    position === "inside" &&
    (targetSearch.node.type === "Section" ||
      targetSearch.node.type === "Container" ||
      targetSearch.node.type === "Card" ||
      targetSearch.node.type === "FeatureGrid")
  ) {
    const insertIntoTarget = (nodes: SiteNode[]): SiteNode[] => {
      return nodes.map((n) => {
        if (n.id === targetId) {
          return { ...n, children: [...(n.children ?? []), nodeToMove] };
        }
        if (n.children && n.children.length > 0) {
          return { ...n, children: insertIntoTarget(n.children) };
        }
        return n;
      });
    };
    return {
      ...docWithoutSource,
      pages: docWithoutSource.pages.map((page) => ({
        ...page,
        nodes: insertIntoTarget(page.nodes),
      })),
    };
  }

  const insertRelative = (nodes: SiteNode[]): SiteNode[] => {
    const idx = nodes.findIndex((n) => n.id === targetId);
    if (idx !== -1) {
      const copy = [...nodes];
      const insertIdx = position === "before" ? idx : idx + 1;
      copy.splice(insertIdx, 0, nodeToMove);
      return copy;
    }
    return nodes.map((n) => {
      if (n.children && n.children.length > 0) {
        return { ...n, children: insertRelative(n.children) };
      }
      return n;
    });
  };

  return {
    ...docWithoutSource,
    pages: docWithoutSource.pages.map((page) => ({
      ...page,
      nodes: insertRelative(page.nodes),
    })),
  };
}
